import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "form-group" }
const _hoisted_2 = { class: "form-group" }
const _hoisted_3 = { value: "" }
const _hoisted_4 = { value: "CASH" }
const _hoisted_5 = { value: "BANK_TRANSFER" }
const _hoisted_6 = { value: "CHECK" }
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = { class: "form-group" }
const _hoisted_10 = { class: "form-actions" }

import { ref, watch } from 'vue';
import AppButton from '@/components/base/AppButton.vue';
import AppModal from '@/components/base/Modal.vue';
import type { PaymentData } from '@/types/services';

interface Props {
  isVisible?: boolean;
  payment?: PaymentData | null;
  isEditMode?: boolean;
  provisioningRequest?: any | null;
}

interface Emits {
  (e: 'cancel'): void;
  (e: 'submit', payload: { data: FormDataType; isValid: boolean }): void;
}

interface FormDataType {
  amount: number | null;
  description: string;
  paymentType: string;
  transactionDate: string;
  invoice: File | null;
  provisioningRequestId?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RecordPaymentModal',
  props: {
    isVisible: { type: Boolean, default: false },
    payment: { default: null },
    isEditMode: { type: Boolean, default: false },
    provisioningRequest: { default: null }
  },
  emits: ["cancel", "submit"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

const emit = __emit;

// Form data
const formData = ref<FormDataType>({
  amount: null,
  description: '',
  paymentType: '',
  transactionDate: new Date().toISOString().split('T')[0],
  invoice: null,
  provisioningRequestId: undefined
});

// Methods
const handleCancel = () => {
  resetForm();
  emit('cancel');
};

const handleSubmit = () => {
  // Validate required fields
  if (!formData.value.amount || !formData.value.paymentType || !formData.value.transactionDate) {
    // Parent component should handle validation error display
    emit('submit', { data: formData.value, isValid: false });
    return;
  }
  
  emit('submit', { data: formData.value, isValid: true });
};

const handleInvoiceUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formData.value.invoice = target.files?.[0] || null;
};

const resetForm = () => {
  if (props.provisioningRequest) {
    // Initialize with provisioning request data
    formData.value = {
      amount: props.provisioningRequest.requestedAmount,
      description: props.provisioningRequest.description || '',
      paymentType: '',
      transactionDate: new Date().toISOString().split('T')[0],
      invoice: null,
      provisioningRequestId: props.provisioningRequest.id
    };
  } else if (props.isEditMode && props.payment) {
    // Initialize with existing payment data for editing
    formData.value = {
      amount: props.payment.amount,
      description: props.payment.description || '',
      paymentType: props.payment.paymentType || '',
      transactionDate: props.payment.transactionDate || new Date().toISOString().split('T')[0],
      invoice: null, // File input will be empty for editing
      provisioningRequestId: undefined
    };
  } else {
    // Initialize with empty data for new payment
    formData.value = {
      amount: null,
      description: '',
      paymentType: '',
      transactionDate: new Date().toISOString().split('T')[0],
      invoice: null,
      provisioningRequestId: undefined
    };
  }
  
  // Reset file input
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
};

// Watch for visibility changes to reset form when modal is opened
watch(() => props.isVisible, (newVal: boolean) => {
  if (newVal) {
    resetForm();
  }
});

// Watch for payment changes to update form data when editing
watch(() => props.payment, () => {
  if (props.isVisible) {
    resetForm();
  }
}, { deep: true });

// Watch for provisioning request changes to update form data
watch(() => props.provisioningRequest, () => {
  if (props.isVisible && props.provisioningRequest) {
    resetForm();
  }
}, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(AppModal, {
      isVisible: _ctx.isVisible,
      title: _ctx.isEditMode ? _ctx.$t('finance.editPayment') : _ctx.$t('finance.recordPayment'),
      onCancel: handleCancel
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleSubmit, ["prevent"]),
          class: "finance-form"
        }, [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("label", null, _toDisplayString(_ctx.$t('finance.amount')), 1),
            _withDirectives(_createElementVNode("input", {
              type: "number",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.amount) = $event)),
              step: "0.01",
              required: "",
              class: "form-input"
            }, null, 512), [
              [_vModelText, formData.value.amount]
            ])
          ]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("label", null, _toDisplayString(_ctx.$t('finance.paymentType')), 1),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.paymentType) = $event)),
              required: "",
              class: "form-input"
            }, [
              _createElementVNode("option", _hoisted_3, _toDisplayString(_ctx.$t('finance.selectPaymentType')), 1),
              _createElementVNode("option", _hoisted_4, _toDisplayString(_ctx.$t('finance.paymentTypes.cash')), 1),
              _createElementVNode("option", _hoisted_5, _toDisplayString(_ctx.$t('finance.paymentTypes.bankTransfer')), 1),
              _createElementVNode("option", _hoisted_6, _toDisplayString(_ctx.$t('finance.paymentTypes.check')), 1)
            ], 512), [
              [_vModelSelect, formData.value.paymentType]
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("label", null, _toDisplayString(_ctx.$t('finance.transactionDate')), 1),
            _withDirectives(_createElementVNode("input", {
              type: "date",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.transactionDate) = $event)),
              required: "",
              class: "form-input"
            }, null, 512), [
              [_vModelText, formData.value.transactionDate]
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", null, _toDisplayString(_ctx.$t('finance.description')), 1),
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.description) = $event)),
              class: "form-input"
            }, null, 512), [
              [_vModelText, formData.value.description]
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("label", null, _toDisplayString(_ctx.$t('finance.invoice')), 1),
            _createElementVNode("input", {
              type: "file",
              onChange: handleInvoiceUpload,
              accept: ".pdf,.jpg,.jpeg,.png",
              class: "form-input"
            }, null, 32)
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(AppButton, {
              type: "button",
              variant: "outline",
              onClick: handleCancel
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(AppButton, {
              type: "submit",
              variant: "primary"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('common.save')), 1)
              ]),
              _: 1
            })
          ])
        ], 32)
      ]),
      _: 1
    }, 8, ["isVisible", "title"])
  ]))
}
}

})